// src/Pages/CurrencyMaster.jsx
import { gradients } from '../utils/theme';
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Box, Grid, TextField, Button, Typography, Snackbar, Alert, Divider } from "@mui/material";
import { useAuth } from '../context/AuthContext';
import { toProperCase } from "../utils/formatRows";
import { getCurrentFYear } from '../utils/financialYear';

export default function CurrencyMaster({ mode, onPopupCancel, onSaved, connectionString, newCurrency }) {
    const { user, selectedCompany } = useAuth();
    const { id } = useParams();
    let userId = user?.UserId;
    const navigate = useNavigate();
    console.log(selectedCompany);
    // pull these from the top‐level prop instead of context
    const companyId = selectedCompany?.CompId;
    const AppRegID = selectedCompany?.AppRegID || null;
    const fYear = getCurrentFYear();
    const username = user.UserName;
    console.log(companyId, userId, username)

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [currencyName, setCurrencyName] = useState(newCurrency || "");
    const [currencySymbol, setCurrencySymbol] = useState("");

    const requiredAsteriskStyle = { '& .MuiFormLabel-asterisk': { color: 'red', }, };

    // when editing, load the existing record
    useEffect(() => {
        if (mode === "edit" && id) {
            axios.get(`${connectionString}/currencymaster/getDetails/${id}`)
                .then((res) => {
                    const data = res.data;
                    setCurrencyName(data.CurrencyName || "");
                    setCurrencySymbol(data.CurrencySymbol || "");
                })
                .catch((err) => console.error("Error fetching currency:", err));
        }
    }, [mode, id]);

    const handleReset = () => {
        if (onPopupCancel) {
            onPopupCancel();
        } else {
            setCurrencyName("");
            setCurrencySymbol("");
            navigate(-1);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = { CurrencyName: currencyName, CurrencySymbol: currencySymbol, companyId,AppRegID, fYear, username, userId };

        try {
            if (mode === "add") {
                await axios.post( `${connectionString}/currencymaster/Insert`, payload );
                setCurrencyName("");
                setCurrencySymbol("");
            } else {
                await axios.put( `${connectionString}/currencymaster/Update/${id}`, payload );
            }
            // after a successful add/update, either close popup or navigate back
            setSnackbar({ open: true, message: mode === "add" ? "Currency added successfully" : "Currency updated successfully", severity: 'success' })
            if (typeof onSaved === "function") {
                onSaved();
            }
            if (typeof onPopupCancel === "function") {
                onPopupCancel()
            }
            else {
                setTimeout(() => { navigate(-1); }, 600);
            }
            // navigate back after 5 seconds
        } catch (err) {
            console.error(err);
            const msg = err.response?.data?.message || "An unexpected error occurred.";
            setSnackbar({ open: true, message: msg, severity: 'error' });
        }
    };

    useEffect(() => {
        if (newCurrency) {
            setCurrencyName(newCurrency);
        } else {
            setCurrencyName("");
        }
    }, [newCurrency]);

    // ──────────────── Alt+S (Save/Update) & Alt+C (Cancel) shortcuts ────────────────
    // Attached to document in the capture phase so it fires regardless of which
    // field/dropdown currently has focus, including MUI popper/portal content.
    // Uses e.code (physical key position) instead of e.key so this also works on
    // macOS, where Option+letter often produces an accented/dead-key character
    // via e.key (e.g. Option+C → "ç") instead of the plain letter.
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (!e.altKey) return;

            if (e.code === "KeyS") {
                e.preventDefault();
                handleSubmit(e);
            } else if (e.code === "KeyC") {
                e.preventDefault();
                handleReset();
            }
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    });

    return (
        <>
            <Box sx={{ height: 4, background: gradients.primaryRow }} />
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: '1.05rem' }}>
                    {mode === "add" ? "Add New Currency" : "Edit Currency"}
                </Typography>
            </Box>
            <Divider />
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <Box sx={{ px: 2.5, pt: 2, pb: 0.5 }} >
                    <Grid container spacing={2} justifyContent={"center"}>
                        <Grid item xs={12}>
                            <TextField label="Currency Name" required fullWidth size="small" value={currencyName} onChange={(e) => setCurrencyName(e.target.value)} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiInputBase-root': { height: 38, alignItems: 'center'}, '& .MuiInputBase-input': { padding: '10px 14px', fontSize: '0.875rem' } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Currency Symbol" required fullWidth size="small" value={currencySymbol} onChange={(e) => setCurrencySymbol(e.target.value)} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiOutlinedInput-root': { height: 38, display: 'flex', alignItems: 'center' }, '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' } }} />
                        </Grid>
                    </Grid>
                </Box>
                <Divider sx={{ mt: 1.5 }} />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button type="button" variant="outlined" color="inherit" size="small" onClick={handleReset}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" size="small" disabled={!currencyName || !currencySymbol}>
                        {mode === "add" ? "Save" : "Update"}
                    </Button>
                </Box>
            </Box>
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}